<section class="content-section page-intro">
  <h2>{{ 'organizationAdmin.users.title' | transloco }}</h2>
  <p>{{ 'organizationAdmin.users.description' | transloco }}</p>
  @if (canEdit()) {
    <div class="page-intro-actions">
      <button
        mat-flat-button
        color="primary"
        type="button"
        (click)="openCreateUserDialog()">
        <mat-icon>person_add</mat-icon>
        {{ 'organizationAdmin.users.toolbar.createUser' | transloco }}
      </button>
    </div>
  }
</section>

<section class="content-section">
  <div class="users-search">
    <mat-form-field appearance="outline" class="users-search-field" subscriptSizing="dynamic">
      <mat-icon matPrefix>search</mat-icon>
      <input matInput [formControl]="searchControl" [placeholder]="'common.buttons.search' | transloco" [attr.aria-label]="'common.buttons.search' | transloco" />
    </mat-form-field>
  </div>

  <div class="users-table-wrapper">
    <table mat-table [dataSource]="pagedRows()" [trackBy]="trackRow">
      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef>{{ 'organizationAdmin.users.columns.name' | transloco }}</th>
        <td mat-cell *matCellDef="let row">
          <ng-container *appSingleBlocSkeleton="isLoading(); width: '70%'">
            <span>{{ row.name }}</span>
            @if (row.isSelf) {
              <span class="self-chip">{{ 'organizationAdmin.users.columns.you' | transloco }}</span>
            }
          </ng-container>
        </td>
      </ng-container>

      <ng-container matColumnDef="email">
        <th mat-header-cell *matHeaderCellDef>{{ 'organizationAdmin.users.columns.email' | transloco }}</th>
        <td mat-cell *matCellDef="let row">
          <ng-container *appSingleBlocSkeleton="isLoading(); width: '80%'">
            <div class="email-value">{{ row.user.email }}</div>
          </ng-container>
        </td>
      </ng-container>

      <ng-container matColumnDef="userName">
        <th mat-header-cell *matHeaderCellDef>{{ 'dataManagement.userData.columns.name' | transloco }}</th>
        <td mat-cell *matCellDef="let row">
          <ng-container *appSingleBlocSkeleton="isLoading(); width: '80%'">
            <div class="email-value">{{ row.user.userName }}</div>
          </ng-container>
        </td>
      </ng-container>

      <ng-container matColumnDef="groups">
        <th mat-header-cell *matHeaderCellDef>{{ 'organizationAdmin.users.columns.groups' | transloco }}</th>
        <td mat-cell *matCellDef="let row">
          <ng-container *appSingleBlocSkeleton="isLoading(); width: '60%'">
            @if (!row.hasNoGroup) {
              <span>{{ row.groupNames }}</span>
            } @else {
              <span class="warn-text">{{ 'organizationAdmin.users.columns.noGroup' | transloco }}</span>
            }
          </ng-container>
        </td>
      </ng-container>

      <ng-container matColumnDef="role">
        <th mat-header-cell *matHeaderCellDef>{{ 'organizationAdmin.users.columns.role' | transloco }}</th>
        <td mat-cell *matCellDef="let row">
          <ng-container *appSingleBlocSkeleton="isLoading(); width: '50%'">
            <span class="role-value" [class.role-elevated]="row.role !== AdminUserRole.Member">
              <mat-icon>{{ row.roleIcon }}</mat-icon>
              {{ ('organizationAdmin.users.roles.' + row.role) | transloco }}
            </span>
          </ng-container>
        </td>
      </ng-container>

      <ng-container matColumnDef="premium">
        <th mat-header-cell *matHeaderCellDef>{{ 'organizationAdmin.users.columns.premium' | transloco }}</th>
        <td mat-cell *matCellDef="let row">
          <ng-container *appSingleBlocSkeleton="isLoading(); width: '60%'">
            <div class="premium-cell">
              <mat-slide-toggle
                color="accent"
                [checked]="row.user.isPremiumUser"
                [attr.aria-label]="'organizationAdmin.users.columns.premium' | transloco"
                [disabled]="!canEdit() || row.isPending || row.user.isDeleted || row.premiumLimitReached"
                [matTooltip]="row.premiumLimitReached ? ('organizationAdmin.users.columns.premiumLimitReached' | transloco) : ''"
                (change)="togglePremium(row.user, $event)">
              </mat-slide-toggle>
              @if (!row.premiumLimitReached) {
                <span>{{ (row.user.isPremiumUser ? 'organizationAdmin.users.columns.premium' : 'organizationAdmin.users.columns.standard') | transloco }}</span>
              } @else {
                <span class="warn-text">{{ 'organizationAdmin.users.columns.premiumLimitReached' | transloco }}</span>
              }
            </div>
          </ng-container>
        </td>
      </ng-container>

      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef>{{ 'organizationAdmin.users.columns.status' | transloco }}</th>
        <td mat-cell *matCellDef="let row">
          <ng-container *appSingleBlocSkeleton="isLoading(); width: '50%'">
            <span class="status-chip" [class.status-active]="!row.user.isDeleted && !row.user.isInactive" [class.status-inactive]="row.user.isDeleted || row.user.isInactive">
              <mat-icon>circle</mat-icon>
              {{ (row.user.isDeleted || row.user.isInactive ? 'organizationAdmin.users.status.inactive' : 'organizationAdmin.users.status.active') | transloco }}
            </span>
          </ng-container>
        </td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let row">
          <ng-container *appSingleBlocSkeleton="isLoading(); width: '36px'; height: '24px'">
            @if (canEdit()) {
              <div class="actions-cell">
                @if (!row.user.isDeleted) {
                  <button mat-icon-button type="button" [matTooltip]="'organizationAdmin.users.actions.edit' | transloco" [attr.aria-label]="'organizationAdmin.users.actions.edit' | transloco" (click)="openEditUserDialog(row)">
                    <mat-icon>edit</mat-icon>
                  </button>
                } @else if (!row.hasHigherRights) {
                  <button mat-icon-button type="button" [matTooltip]="'organizationAdmin.users.actions.reactivate' | transloco" [attr.aria-label]="'organizationAdmin.users.actions.reactivate' | transloco" (click)="toggleUserActive(row)">
                    <mat-icon>restart_alt</mat-icon>
                  </button>
                }
                @if (row.user.isPasswordLoginAllowed && !row.hasHigherRights && !row.isSelf) {
                  <button mat-icon-button type="button" [matTooltip]="'organizationAdmin.users.actions.resetPassword' | transloco" [attr.aria-label]="'organizationAdmin.users.actions.resetPassword' | transloco" (click)="resetPassword(row.user)">
                    <mat-icon>lock_reset</mat-icon>
                  </button>
                }
                @if (!row.user.isDeleted && (row.isSelfLeadAdmin || (!row.isSelf && !row.hasHigherRights))) {
                  <button
                    mat-icon-button
                    type="button"
                    [matTooltip]="(row.isSelfLeadAdmin ? 'organizationAdmin.users.actions.transferLeadAdmin' : 'organizationAdmin.users.actions.deactivate') | transloco"
                    [attr.aria-label]="(row.isSelfLeadAdmin ? 'organizationAdmin.users.actions.transferLeadAdmin' : 'organizationAdmin.users.actions.deactivate') | transloco"
                    (click)="toggleUserActive(row)">
                    <mat-icon>{{ row.isSelfLeadAdmin ? 'swap_horizontal_circle' : 'person_off' }}</mat-icon>
                  </button>
                }
              </div>
            }
          </ng-container>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns()"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns()" [class.deactivated]="row.user.isDeleted" [class.highlighted]="row.user.id === highlightedUserId()"></tr>
      <tr class="mat-row" *matNoDataRow>
        <td class="mat-cell no-data-cell" [attr.colspan]="displayedColumns().length">
          @if (loadFailed()) {
            <mat-icon>error_outline</mat-icon>
            {{ 'organizationAdmin.users.loadFailed' | transloco }}
          } @else {
            <mat-icon>person_off</mat-icon>
            {{ 'organizationAdmin.users.noUsers' | transloco }}
          }
        </td>
      </tr>
    </table>
  </div>

  <div class="pagination-row">
    <div class="users-footer-actions">
      <button mat-stroked-button class="column-organizer-button" type="button" (click)="toggleShowDeactivated()">
        {{ (showDeactivated() ? 'organizationAdmin.users.footer.hideDeactivated' : 'organizationAdmin.users.footer.showDeactivated') | transloco }}
      </button>
      <button mat-stroked-button class="column-organizer-button" type="button" (click)="openColumnOrganizer()">
        <mat-icon>view_column</mat-icon>
        {{ 'organizationAdmin.users.toolbar.columns' | transloco }}
      </button>
    </div>
    <mat-paginator
      [pageSizeOptions]="pageSizeOptions"
      [pageSize]="pageSize()"
      [length]="rows().length"
      [pageIndex]="effectivePageIndex()"
      showFirstLastButtons
      [attr.aria-label]="'organizationAdmin.users.title' | transloco"
      (page)="onPageChanged($event)"></mat-paginator>
  </div>
</section>
